// Implements: F-UIX-018, F-USG-005, F-USG-006
/**
 * What "Consumo por ámbito" (UIX.5) can show a person, and how a row is named. Plain data in,
 * plain data out.
 *
 * A target is an organization or a team whose consumption the server lets the person read,
 * taken from the server's permission answer on the session's scopes (UIX.5): an organization
 * scope that carries `usage:read_organization` (its org admin), a team scope that carries
 * `usage:read_team` (its lead, or an org admin seated in it). No role name is compared. This
 * only decides what the page OFFERS: the server decides every read
 * again and answers a reader it refuses with the `404` of a path that does not exist, which
 * the page shows as an error, never as somebody else's figures.
 */

import type { BackofficeScope } from "../../../types/api";
import type { ConsumptionGrouping, ConsumptionRowView, ReportTarget } from "../../../types/reports";
import { calendarDayText } from "./myUsage";
import { permits } from "./sections";

export type { ReportTarget };

/** The organizations the person may read, then the teams they may read, in session order. */
export function reportTargetsOf(scopes: readonly BackofficeScope[]): ReportTarget[] {
  const organizations: ReportTarget[] = [];
  const teams: ReportTarget[] = [];
  for (const scope of scopes) {
    if (scope.id === null) continue;
    if (scope.kind === "organization" && permits(scope, "usage:read_organization")) {
      organizations.push({ kind: "organization", organizationId: scope.id, name: scope.name });
    }
    // A team the session cannot place in an organization has no report address.
    const organizationId = scope.organization_id ?? null;
    if (scope.kind === "team" && permits(scope, "usage:read_team") && organizationId !== null) {
      teams.push({ kind: "team", organizationId, teamId: scope.id, name: scope.name });
    }
  }
  return [...organizations, ...teams];
}

/** One stable key per target: what keys its container and tells two targets apart. */
export function targetKey(target: ReportTarget): string {
  return target.kind === "organization"
    ? `organization:${target.organizationId}`
    : `team:${target.organizationId}:${target.teamId}`;
}

/**
 * The groupings a target offers, the first one shown first: a team folds `team` into one row,
 * so it is not offered, and a team opens on its members.
 */
export function groupingsOf(target: ReportTarget): readonly ConsumptionGrouping[] {
  // Mockup v3 P9 (accepted proposal I.2): the detail groups by person or team; the chart is by day.
  return target.kind === "organization" ? ["person", "team"] : ["person"];
}

/** How a row's subject reads: a team name the caller resolved, or one of these. */
export type RowSubject =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "noTeam" }
  | { readonly kind: "unknownTeam" }
  | { readonly kind: "noPerson" };

/**
 * The subject of one row under `grouping`: its day (in the server's calendar, never the
 * browser's zone), its team or its person (the server's own words for a deleted or
 * unattributed one).
 */
export function rowSubject(
  row: ConsumptionRowView,
  grouping: ConsumptionGrouping,
  teamNames: ReadonlyMap<string, string>,
): RowSubject {
  switch (grouping) {
    case "day":
      return { kind: "text", text: calendarDayText(row.day ?? "") };
    case "team": {
      if (row.team_id === null) return { kind: "noTeam" };
      const name = teamNames.get(row.team_id);
      return name === undefined ? { kind: "unknownTeam" } : { kind: "text", text: name };
    }
    case "person":
      // The server names every person row, deleted and unattributed included (S11a-1).
      return row.person === null ? { kind: "noPerson" } : { kind: "text", text: row.person.name };
  }
}
